/**
 * Centralized builders for notification `actionUrl` deep links.
 *
 * Every notification's tappable destination should be produced here so routes
 * are defined in one place.
 *
 * Comment links carry the target comment in a query param (read by the detail
 * page to open the comments sheet and scroll to it) rather than a `#hash`,
 * which the Next router and the drawer-based comment UI handle reliably.
 */

type Id = string | number;

/** Query param used to deep-link to a specific comment within a thread. */
export const NOTIFICATION_COMMENT_PARAM = "comment";
export const NOTIFICATION_FALLBACK_ACTION_URL = "/notification";

const TRUSTED_NOTIFICATION_HOSTS = new Set([
  "hiive.club",
  "www.hiive.club",
  "localhost",
  "127.0.0.1",
  "10.0.2.2",
]);

const isTrustedNotificationHost = (hostname: string) =>
  TRUSTED_NOTIFICATION_HOSTS.has(hostname) || hostname.endsWith(".hiive.club");

const normalizePathLikeNotificationUrl = (path: string): string =>
  path.startsWith("/") ? path : `/${path}`;

/**
 * Notifications are app-internal navigation. Reject malformed or external
 * values before they reach Next Link/router navigation.
 */
export function normalizeNotificationActionUrl(
  actionUrl: string | null | undefined,
): string {
  const raw = actionUrl?.trim();
  if (!raw) return NOTIFICATION_FALLBACK_ACTION_URL;

  if (raw.startsWith("/") && !raw.startsWith("//")) {
    return normalizePathLikeNotificationUrl(raw);
  }

  try {
    const url = new URL(raw);

    if (url.protocol === "hiive:") {
      const path = `${url.host}${url.pathname}${url.search}${url.hash}`;
      return path ? normalizePathLikeNotificationUrl(path) : "/";
    }

    if (
      (url.protocol === "https:" || url.protocol === "http:") &&
      isTrustedNotificationHost(url.hostname)
    ) {
      return normalizePathLikeNotificationUrl(
        `${url.pathname}${url.search}${url.hash}`,
      );
    }
  } catch {
    return NOTIFICATION_FALLBACK_ACTION_URL;
  }

  return NOTIFICATION_FALLBACK_ACTION_URL;
}

/**
 * Parse the `?comment=` query value into a positive comment id, or null.
 * Used by detail pages to decide whether to open + scroll to a comment.
 */
export const parseCommentParam = (value: string | null): number | null => {
  if (!value) return null;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const withComment = (basePath: string, commentId: Id): string =>
  `${basePath}?${NOTIFICATION_COMMENT_PARAM}=${commentId}`;

export const notificationLinks = {
  /** Activity session detail. Route: /sessions/[id]. */
  session: (sessionId: Id): string => `/sessions/${sessionId}`,
  /** Activity session detail, scrolled to a comment. */
  sessionComment: (sessionId: Id, commentId: Id): string =>
    withComment(`/sessions/${sessionId}`, commentId),

  /** Community post detail. Route: /post/[id]. */
  post: (postId: Id): string => `/post/${postId}`,
  /** Post detail, scrolled to a comment. */
  postComment: (postId: Id, commentId: Id): string =>
    withComment(`/post/${postId}`, commentId),

  /** Activity post detail. Route: /activity-post/[id]. */
  activityPost: (activityPostId: Id): string =>
    `/activity-post/${activityPostId}`,
  /** Activity post detail, scrolled to a comment. */
  activityPostComment: (activityPostId: Id, commentId: Id): string =>
    withComment(`/activity-post/${activityPostId}`, commentId),

  /** Public profile. Route: /profile/[handle]. Handle must resolve to a username. */
  profile: (handle: string): string => `/profile/${handle}`,

  /** Event detail within a hiive. Route: /hiives/[hiiveSlug]/events/[eventSlug]. */
  event: (hiiveSlug: string, eventSlug: string): string =>
    `/hiives/${hiiveSlug}/events/${eventSlug}`,

  /**
   * The Hiive card opened on its Stickers tab. Route: /wallet, where `card`
   * and `tab` drive the card sheet (see wallet-page-client).
   */
  walletStickers: (): string => "/wallet?card=open&tab=stickers",
} as const;
